/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CustomFilterV2, EmptyState, TableV2 } from '../../../components/v2';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as actionsNsir from '../../../store/nsir/actions';
import { canViewInspecaoReport } from '../../../helpers/inspecaoReportPermission';
import TableService from '../../../services/TableService';
import ChartsRow from './ChartsRow';
import KpiStrip from './KpiStrip';
import { formatAdereMonthLabel } from './chartShared';
import {
  EMPTY_RESUMO,
  STATUS_FILTER_OPTIONS,
  formatPeriodLabel,
  getDefaultFilter,
  normalizeFilterForApi,
  normalizeResumo,
  resolveKpiStatusFilter,
  VENCIMENTO_URGENCY_LABELS,
} from './constants';
import {
  renderDatasCol,
  renderEquipamentoCol,
  renderMalhaCol,
  renderStatusCol,
} from './tableCells';

const RELATORIO_INSPECAO_DATE_TYPES = [
  'DATA_CRIACAO',
  // 'DATA_DIGITACAO',
  // 'DATA_EXECUTADA_GDIS',
  'DATA_INSPECAO',
  'DATA_VENCIMENTO',
];

const RelatorioInspecaoV2 = ({ authData, generateInspecaoReport }) => {
  const canView = canViewInspecaoReport({ authData });

  const defaultFilter = useMemo(
    () => ({
      ...getDefaultFilter(authData, false),
      dateTypes: RELATORIO_INSPECAO_DATE_TYPES,
    }),
    [authData],
  );

  const [localFilter, setLocalFilter] = useState(defaultFilter);
  const [activeStatus, setActiveStatus] = useState(null);
  const [activeVencimentoUrgency, setActiveVencimentoUrgency] = useState(null);
  const [activeAdereMonth, setActiveAdereMonth] = useState(null);
  const [activeAdereSegment, setActiveAdereSegment] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [reload, setReload] = useState(false);
  const [resumo, setResumo] = useState(EMPTY_RESUMO);
  const [resumoLoading, setResumoLoading] = useState(false);

  const apiFilterBase = useMemo(
    () => normalizeFilterForApi(localFilter),
    [localFilter],
  );

  const apiFilter = useMemo(() => {
    let filter = { ...localFilter };

    const statusFilter = resolveKpiStatusFilter(activeStatus);
    if (statusFilter?.length) {
      filter = { ...filter, status: statusFilter };
    }

    if (activeAdereMonth) {
      filter = {
        ...filter,
        adereMonths: [activeAdereMonth],
        possuiAdere: 'true',
      };
    }

    if (activeAdereSegment?.malhaId) {
      filter = { ...filter, malhaId: [activeAdereSegment.malhaId] };
    }

    if (activeAdereSegment?.status) {
      filter = { ...filter, status: [activeAdereSegment.status] };
    }

    if (activeVencimentoUrgency) {
      filter = { ...filter, vencimentoUrgency: activeVencimentoUrgency };
    }

    return normalizeFilterForApi(filter);
  }, [localFilter, activeStatus, activeAdereMonth, activeAdereSegment, activeVencimentoUrgency]);

  const handleSelectStatus = useCallback((statusKey) => {
    setActiveStatus(statusKey);
    if (statusKey) {
      setActiveVencimentoUrgency(null);
    }
  }, []);

  const handleVencimentoUrgencyChange = useCallback((urgencyKey) => {
    setActiveVencimentoUrgency(urgencyKey);
    if (urgencyKey) {
      setActiveStatus(null);
    }
  }, []);

  const clearVencimentoUrgency = useCallback(() => {
    setActiveVencimentoUrgency(null);
  }, []);

  const handleAdereMonthChange = useCallback((month) => {
    setActiveAdereMonth(month);
    setActiveAdereSegment(null);
  }, []);

  const handleAdereSegmentSelect = useCallback((segment) => {
    if (!segment) {
      setActiveAdereSegment(null);
      return;
    }
    setActiveAdereSegment({
      malhaId: segment.malhaId,
      status: segment.status,
    });
  }, []);

  const clearAdereDrill = useCallback(() => {
    setActiveAdereMonth(null);
    setActiveAdereSegment(null);
  }, []);

  const periodoLabel = useMemo(
    () => formatPeriodLabel(localFilter.inicio, localFilter.fim),
    [localFilter.inicio, localFilter.fim],
  );

  const fetchResumo = useCallback(async () => {
    if (!canView) return;
    setResumoLoading(true);
    try {
      const response = await TableService.request(
        'reports/inspecao/resumo',
        apiFilterBase,
      );
      setResumo(normalizeResumo(response?.data));
    } catch {
      setResumo(EMPTY_RESUMO);
    } finally {
      setResumoLoading(false);
    }
  }, [apiFilterBase, reload, canView]);

  useEffect(() => {
    fetchResumo();
  }, [fetchResumo]);

  const onExport = () => {
    generateInspecaoReport(normalizeFilterForApi(localFilter), () => {});
  };

  const columns = useMemo(
    () => [
      {
        field: 'equipamentoCol',
        headerName: 'NSIR e Equipamento',
        flex: 1.2,
        minWidth: 180,
        sortable: false,
        renderCell: (params) => renderEquipamentoCol(params.row),
      },
      {
        field: 'inspetor',
        headerName: 'Inspetor',
        flex: 1,
        minWidth: 120,
        sortable: false,
      },
      {
        field: 'malhaCol',
        headerName: 'Malha / Gerência',
        flex: 1,
        minWidth: 140,
        sortable: false,
        renderCell: (params) => renderMalhaCol(params.row),
      },
      {
        field: 'statusCol',
        headerName: 'Status',
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: (params) => renderStatusCol(params.row),
      },
      {
        field: 'datasCol',
        headerName: 'Datas',
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => renderDatasCol(params.row),
      },
    ],
    [],
  );

  if (!canView) {
    return (
      <Box>
        <EmptyState
          title="Acesso restrito"
          message="O Relatório de Inspeção está disponível apenas para usuários marcados como supervisor."
        />
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ pr: 2 }}
        >
          <Box>
            <Typography variant="h4">Relatório de Inspeção</Typography>
            {periodoLabel && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                Período: {periodoLabel}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<FilterListOutlinedIcon />}
              onClick={() => setOpenFilter(true)}
            >
              Filtros
            </Button>
            <Button
              variant="contained"
              startIcon={<DescriptionOutlinedIcon />}
              onClick={onExport}
            >
              Exportar
            </Button>
          </Stack>
        </Stack>

        <KpiStrip
          resumo={resumo}
          loading={resumoLoading}
          activeStatus={activeStatus}
          onSelectStatus={handleSelectStatus}
        />

        <ChartsRow
          apiFilterBase={apiFilterBase}
          refreshKey={reload}
          selectedAdereMonth={activeAdereMonth}
          onAdereMonthChange={handleAdereMonthChange}
          onAdereSegmentSelect={handleAdereSegmentSelect}
          activeVencimentoUrgency={activeVencimentoUrgency}
          onVencimentoUrgencyChange={handleVencimentoUrgencyChange}
        />

        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, pr: 2 }} flexWrap="wrap" useFlexGap>
            <Stack direction="row" alignItems="center" spacing={1}>
              <InfoOutlinedIcon fontSize="small" color="action" />
              <Typography variant="h6">Detalhamento</Typography>
            </Stack>
            {activeAdereMonth && (
              <Chip
                size="small"
                label={`Filtrado: ${formatAdereMonthLabel(activeAdereMonth)}`}
                onDelete={clearAdereDrill}
                sx={{ fontWeight: 500 }}
              />
            )}
            {activeVencimentoUrgency && (
              <Chip
                size="small"
                label={`Filtrado: ${VENCIMENTO_URGENCY_LABELS[activeVencimentoUrgency] || activeVencimentoUrgency}`}
                onDelete={clearVencimentoUrgency}
                sx={{ fontWeight: 500 }}
              />
            )}
          </Stack>
          <TableV2
            useApi
            fetchUrl="reports/inspecao/filter"
            paginationUrl="/v2/relatorio-inspecao"
            filterData={apiFilter}
            refreshKey={reload}
            rowHeight={72}
            pageSize={10}
            columns={columns}
            emptyMessage="Nenhuma NSIR encontrada"
          />
        </Box>

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          defaultFilter={{
            ...defaultFilter,
            dateTypes: RELATORIO_INSPECAO_DATE_TYPES,
          }}
          appliedFilter={localFilter}
          confirmButtonText="Aplicar"
          fields={[
            { field: 'useDates', useDateTypes: true },
            {
              field: 'useInspetor',
              multiSelect: true,
              md: 6,
              xs: 12,
            },
            { field: 'useCodigo', codigoLabel: 'Código NSIR', md: 6, xs: 12 },
            { field: 'useMalha', md: 4, xs: 12, multiSelect: true },
            { field: 'useGerencia', md: 4, xs: 12 },
            { field: 'usePolo', md: 4, xs: 12 },
            { field: 'useSubestacao', md: 4, xs: 12 },
            { field: 'useAlimentador', md: 4, xs: 12 },
            { field: 'useTipoServico', md: 6, xs: 12 },
            { field: 'useAdere' },
            {
              field: 'useNsirStatus',
              customStatus: STATUS_FILTER_OPTIONS,
              multiSelect: true,
              filterKey: 'status',
              md: 12,
              xs: 12,
            },
          ]}
          onFilter={(filterData) => {
            setLocalFilter(filterData);
            setActiveStatus(null);
            setActiveVencimentoUrgency(null);
            clearAdereDrill();
            setOpenFilter(false);
            setReload((prev) => !prev);
          }}
        />
      </Stack>
    </Box>
  );
};

RelatorioInspecaoV2.propTypes = {
  authData: PropTypes.object.isRequired,
  generateInspecaoReport: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      generateInspecaoReport: actionsNsir.generateInspecaoReport,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(RelatorioInspecaoV2);
